import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, AlertTriangle, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth, pickPostLoginPath } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { detectBrowserTimezone } from "@/lib/timezones";

/**
 * Phase 4b Step 1 — Google OAuth callback handler.
 *
 * Mounted at `/auth/google` (the redirect URI registered in Google Cloud
 * Console). Reads `?code=&state=` from the URL, validates that `state`
 * matches the value stored in sessionStorage at redirect-time, then POSTs
 * `{code, state, role}` to `/api/auth/google`. On success: store JWT via
 * AuthContext.setSession and redirect by capability. On 409
 * email_collision: surface a clear modal pointing the user to the
 * password-login + manual link path.
 */
export default function GoogleAuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setSession, refreshMe } = useAuth();
  const [phase, setPhase] = useState("exchanging"); // exchanging | email_collision | error
  const [errorDetail, setErrorDetail] = useState("");
  // Guard against React 18 StrictMode double-fire (effect runs twice in dev).
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    const code = params.get("code");
    const stateFromUrl = params.get("state");
    const errorFromGoogle = params.get("error");

    // Google itself may surface an error via the `error` param (e.g.,
    // user clicked "deny"). Surface a clean message and route back.
    if (errorFromGoogle) {
      setPhase("error");
      setErrorDetail(
        errorFromGoogle === "access_denied"
          ? "You cancelled the Google sign-in."
          : `Google returned an error: ${errorFromGoogle}`,
      );
      sessionStorage.removeItem("pp_google_oauth_state");
      sessionStorage.removeItem("pp_google_oauth_role_hint");
      return;
    }

    if (!code || !stateFromUrl) {
      setPhase("error");
      setErrorDetail("Missing code or state parameter from Google.");
      return;
    }

    const storedState = sessionStorage.getItem("pp_google_oauth_state");
    if (!storedState || storedState !== stateFromUrl) {
      setPhase("error");
      setErrorDetail(
        "Google sign-in state mismatch. This can happen if you opened the link in a different browser tab. Please try again.",
      );
      sessionStorage.removeItem("pp_google_oauth_state");
      sessionStorage.removeItem("pp_google_oauth_role_hint");
      return;
    }

    const roleHint = sessionStorage.getItem("pp_google_oauth_role_hint") || undefined;

    const exchange = async () => {
      try {
        const { data } = await api.post("/auth/google", {
          code, state: stateFromUrl, role: roleHint,
          // Track B — send browser-detected timezone. Backend field
          // validator accepts null/absent for legacy-client tolerance
          // but persists it into the profile shell when present +
          // valid, so first-time OAuth signups get their timezone
          // captured on the same request that creates the account.
          timezone: detectBrowserTimezone() || null,
        });
        setSession(data.access_token, data.user);
        await refreshMe();
        toast.success(`Signed in with Google as ${data.user.email}`);
        sessionStorage.removeItem("pp_google_oauth_state");
        sessionStorage.removeItem("pp_google_oauth_role_hint");
        navigate(pickPostLoginPath(data.user) || "/", { replace: true });
      } catch (err) {
        const body = err.response?.data || {};
        sessionStorage.removeItem("pp_google_oauth_state");
        sessionStorage.removeItem("pp_google_oauth_role_hint");
        if (err.response?.status === 409
            && body.error_code === "email_collision_requires_explicit_link") {
          setPhase("email_collision");
          setErrorDetail(body.detail || "An account with this email already exists.");
        } else {
          setPhase("error");
          setErrorDetail(formatApiErrorDetail(body.detail) || err.message);
        }
      }
    };
    exchange();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Fire-once-on-mount: the OAuth `code+state` URL params are single-use (backend consumes the state nonce on first call; a re-call would 4xx with `invalid_oauth_state`). Re-adding `navigate / params / refreshMe / setSession` would either no-op (stable identities) or, for `params`, re-fire on every URL change and cause the double-exchange bug. The `fired` ref above guards StrictMode's dev-only double-effect.

  return (
    <div className="surface-soft flex min-h-screen items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <Link to="/" className="block">
            <Logo variant="full" className="h-24 w-24" />
          </Link>
        </div>

        {phase === "exchanging" && (
          <div
            className="flex flex-col items-center gap-4 rounded-3xl border border-border bg-pp-surface p-8 text-center"
            data-testid="google-callback-exchanging"
          >
            <Loader2 className="h-10 w-10 animate-spin text-brand-orange" />
            <h1 className="font-heading text-2xl font-bold text-pp-text">
              Finishing your sign-in…
            </h1>
            <p className="text-sm text-muted-foreground">
              Talking to Google. This usually takes just a second.
            </p>
          </div>
        )}

        {phase === "email_collision" && (
          <div
            className="flex flex-col items-center gap-4 rounded-3xl border border-amber-200 bg-amber-50/70 p-8 text-center"
            data-testid="google-callback-email-collision"
          >
            <ShieldAlert className="h-10 w-10 text-amber-600" />
            <h1 className="font-heading text-2xl font-bold text-pp-text">
              Account already exists
            </h1>
            <p className="text-sm text-muted-foreground">{errorDetail}</p>
            <p className="text-xs text-muted-foreground">
              Log in with your password first, then link Google from your account settings.
            </p>
            <Button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="mt-2 rounded-full bg-pp-purple px-6 text-white hover:bg-pp-purple-dark"
              data-testid="google-callback-go-to-login"
            >
              Go to login
            </Button>
          </div>
        )}

        {phase === "error" && (
          <div
            className="flex flex-col items-center gap-4 rounded-3xl border border-red-200 bg-red-50/70 p-8 text-center"
            data-testid="google-callback-error"
          >
            <AlertTriangle className="h-10 w-10 text-red-600" />
            <h1 className="font-heading text-2xl font-bold text-pp-text">
              Google sign-in failed
            </h1>
            <p className="text-sm text-muted-foreground">{errorDetail}</p>
            <Button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              variant="outline"
              className="mt-2 rounded-full px-6"
              data-testid="google-callback-back-to-login"
            >
              Back to login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
